import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useEffect, useState } from "react";
import {
  createSecureMessageListener,
  sendSecurePostMessage,
} from "../../../shared/utils/postmessage-validation";
import {
  type TransactionRequest,
  type TransactionResponse,
  type WalletAccount,
  WalletAdapterStatus,
} from "../utils/types";
import { WalletContext, type WalletContextType } from "./shared-wallet-context";

interface ExternalWalletProviderProps {
  children: ReactNode;
}

interface WalletStateMessage {
  type: "walletState";
  data: {
    isConnected: boolean;
    address?: string;
    chainId?: string;
    /**
     * CAIP-2 format chain identifier from external wallet provider.
     * This is mapped to our internal `caip2ChainId` property in WalletAccount.
     * External providers may use `caipNetworkId` naming (matching AppKit convention).
     */
    caipNetworkId?: string;
    walletInfo?: string;
    namespace?: string;
  };
}

interface SendTransactionResponseMessage {
  type: "sendTransactionResponse";
  success: boolean;
  data?: TransactionResponse;
  error?: string;
}

type WalletMessage = WalletStateMessage | SendTransactionResponseMessage;

export function ExternalWalletProvider({ children }: ExternalWalletProviderProps) {
  const { t } = useLingui();
  const [status, setStatus] = useState<WalletAdapterStatus>(WalletAdapterStatus.DISCONNECTED);
  const [selectedAccount, setSelectedAccount] = useState<WalletAccount | null>(null);
  const [pendingTransaction, setPendingTransaction] = useState<{
    resolve: (value: TransactionResponse) => void;
    reject: (error: Error) => void;
  } | null>(null);
  const [pendingSwitchNetwork, setPendingSwitchNetwork] = useState<{
    resolve: (value: boolean) => void;
    reject: (error: Error) => void;
    supportedChains: string[];
  } | null>(null);

  // Handle messages from parent with secure validation
  useEffect(() => {
    const handleMessage = createSecureMessageListener(
      (data: WalletMessage) => {
        if (data.type === "walletState") {
          const walletState = data.data;

          setStatus(
            walletState.isConnected
              ? WalletAdapterStatus.CONNECTED
              : WalletAdapterStatus.DISCONNECTED,
          );

          setSelectedAccount(
            walletState.isConnected && walletState.address
              ? {
                  address: walletState.address,
                  chainId: walletState.chainId || "1",
                  // Map external wallet's caipNetworkId to our internal caip2ChainId
                  caip2ChainId: walletState?.caipNetworkId ?? "eip155:1",
                  name: walletState.walletInfo || t`External Wallet`,
                  namespace: walletState.namespace || "eip155",
                }
              : null,
          );

          // Check if we're waiting for a network switch and if the chainId is now supported
          if (
            pendingSwitchNetwork &&
            walletState.chainId &&
            pendingSwitchNetwork.supportedChains.includes(walletState.chainId)
          ) {
            pendingSwitchNetwork.resolve(true);
            setPendingSwitchNetwork(null);
          }
        } else if (data.type === "sendTransactionResponse") {
          const { success, data: responseData, error } = data;

          if (pendingTransaction) {
            if (success) {
              pendingTransaction.resolve(responseData as TransactionResponse);
            } else {
              pendingTransaction.reject(new Error(error || t`Transaction sending failed`));
            }
            setPendingTransaction(null);
          }
        }
      },
      {
        expectedTypes: ["walletState", "sendTransactionResponse"],
      },
    );

    window.addEventListener("message", handleMessage);

    // Request initial wallet state using secure postMessage
    sendSecurePostMessage(window.parent, { type: "getWalletState" });

    return () => window.removeEventListener("message", handleMessage);
  }, [pendingTransaction, pendingSwitchNetwork, t]);

  const connect = async (): Promise<void> => {
    sendSecurePostMessage(window.parent, { type: "connectWallet" });
  };

  const disconnect = async (): Promise<void> => {
    sendSecurePostMessage(window.parent, { type: "disconnectWallet" });
  };

  const sendTransaction = async (tx: TransactionRequest): Promise<TransactionResponse> => {
    return new Promise((resolve, reject) => {
      // Store the promise handlers
      setPendingTransaction({ resolve, reject });

      // Send the transaction request using secure postMessage
      sendSecurePostMessage(window.parent, {
        type: "sendTransaction",
        data: tx,
      });
    });
  };

  const switchChain = async (supportedChains?: string[]): Promise<boolean> => {
    if (!supportedChains || supportedChains.length === 0) {
      // External mode requires supported chains list
      return false;
    }

    return new Promise<boolean>((resolve, reject) => {
      // Store the promise handlers with supported chains
      setPendingSwitchNetwork({
        resolve,
        reject: (error: Error) => reject(error),
        supportedChains,
      });

      // Send the switch network request using secure postMessage
      sendSecurePostMessage(window.parent, {
        type: "switchNetwork",
        data: { supportedChains },
      });

      // No explicit response needed - we'll resolve when walletState updates with a supported chainId
    });
  };

  const contextValue: WalletContextType = {
    status,
    selectedAccount,
    connect,
    disconnect,
    sendTransaction,
    switchChain,
  };

  return <WalletContext.Provider value={contextValue}>{children}</WalletContext.Provider>;
}
